import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { ENV } from '@environment';
import { ThemeProps, ThemeProvider, withTheme } from 'styled-components';

import { hotelsSearchParameters } from 'action-creators/hotels';
import { ReactComponent as IconArrival } from 'assets/images/search_arrival.svg';
import { ReactComponent as IconCalendar } from 'assets/images/search_calendar.svg';
import DateRangePicker from 'components/DateRangePicker';
import { customTheme } from 'components/DesktopFlightsSearch/customTheme';
import BookingSelectDate, { RenderProps as BookingSelectRenderProps } from 'components/Selects/BookingSelectDate';
import BookingSelectLocation from 'components/Selects/BookingSelectLocation';
import BranchCompanySelect from 'components/Selects/BranchCompanySelect';
import TravelArrangerSelect from 'components/Selects/TravelArrangerSelect';
import HotelsLocationSearchContainer from 'containers/HotelsLocationSearch';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useUserAccount from 'hooks/useUserAccount';
import { GetAllBillingCompanyResponse } from 'models/billingCompany';
import { GroupedListModel } from 'models/groupedList';
import { TravelHotelContentLocationRequest } from 'models/hotelLocation';
import ResponseScreen, { BackgroundTypes } from 'screens/components/ResponseScreen';
import { amplitudelogEvent, SearchEvent } from 'services/amplitudeService';
import { Guest } from 'services/userGuestService';
import { TravelArrangerUsersAllowed } from 'services/userService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { dateFormatWeekend, daysCount } from 'utils/generic';

import { Container, Fields, Field, SearchButton, Icon, IconSearch } from './styles';

interface Props extends ThemeProps<any> {
  defaultTraveler?: TravelArrangerUsersAllowed;
  defaultDestinationPoint?: TravelHotelContentLocationRequest;
  defaultCheckInDate?: Date;
  defaultCheckOutDate?: Date;
  allowedTravelers: TravelArrangerUsersAllowed[];
  isLoadingAllowedTravelers: boolean;
  isLoading?: boolean;
  isHome?: boolean;
  onClose?: () => void;
  branchCompanies?: GetAllBillingCompanyResponse[];
  isLoadingBranchCompanies: boolean;
  defaultBranchCompany?: GetAllBillingCompanyResponse;
}

const travelerToGroupedModel = (traveler: TravelArrangerUsersAllowed): GroupedListModel => ({
  id: traveler.id,
  name: traveler.name,
  lastName: traveler.lastName,
  favorite: traveler.favorite,
  email: traveler.email,
  userGroupId: traveler.userGroupId,
  userGroupName: traveler.userGroupName,
  entity: traveler.guest ? 'guest' : 'user',
});

const branchCompanyToGroupedModel = (branchCompany: GetAllBillingCompanyResponse): GroupedListModel => ({
  id: branchCompany.id,
  name: branchCompany.corporateName,
});

const DesktopHotelsSearch = ({
  defaultTraveler,
  defaultDestinationPoint,
  defaultCheckInDate,
  defaultCheckOutDate,
  allowedTravelers,
  isLoadingAllowedTravelers,
  isLoading,
  isHome = true,
  onClose,
  branchCompanies,
  isLoadingBranchCompanies,
  defaultBranchCompany,
}: Props) => {
  const history = useHistory();
  const location = useLocation<{ showNoResults?: boolean } | null>();
  const { t } = useStoreTranslation();
  const user = useUserAccount();
  const dispatch = useDispatch();

  const [showNoResults, setShowNoResults] = useState(location.state?.showNoResults || false);
  const [destinationPoint, setDestinationPoint] = useState<TravelHotelContentLocationRequest | undefined>(
    defaultDestinationPoint,
  );
  const [checkInDate, setCheckInDate] = useState<Date | undefined>(defaultCheckInDate);
  const [checkOutDate, setCheckOutDate] = useState<Date | undefined>(defaultCheckOutDate);
  const [traveler, setTraveler] = useState<TravelArrangerUsersAllowed | undefined>(defaultTraveler);
  const [branchCompany, setBranchCompany] = useState<GetAllBillingCompanyResponse | undefined>(defaultBranchCompany);

  useEffect(() => {
    history.replace({ ...history.location, state: undefined });
  }, [history]);

  useEffect(() => {
    setTraveler(defaultTraveler);
  }, [defaultTraveler]);

  useEffect(() => {
    setBranchCompany(defaultBranchCompany);
  }, [defaultBranchCompany]);

  const isValid = useMemo(() => {
    const hasTraveler = !user?.isTravelArranger || (user?.isTravelArranger && traveler);
    return Boolean(destinationPoint && checkInDate && checkOutDate && hasTraveler);
  }, [user, traveler, destinationPoint, checkInDate, checkOutDate]);

  const isBranchCompanyValid = useMemo(() => {
    if (branchCompanies && branchCompanies.length > 0) {
      if (branchCompany === undefined) {
        return false;
      }
    }
    return true;
  }, [branchCompanies, branchCompany]);

  const transformedTravelerList: GroupedListModel[] = useMemo(() => allowedTravelers.map(travelerToGroupedModel), [
    allowedTravelers,
  ]);
  const transformedSelectedTraveler: GroupedListModel | undefined = useMemo(() => {
    if (!traveler) return undefined;
    return travelerToGroupedModel(traveler);
  }, [traveler]);

  const transformedSelectedBranchCompany: GroupedListModel | undefined = useMemo(() => {
    if (!branchCompany) return undefined;
    return branchCompanyToGroupedModel(branchCompany);
  }, [branchCompany]);

  const handleChangeDateSelect = (dates: { initialDate: Date; finalDate: Date }) => {
    setCheckInDate(dates?.initialDate);
    setCheckOutDate(dates?.finalDate);
  };

  const handleClickSearchButton = useCallback(async () => {
    if (destinationPoint && checkInDate && checkOutDate && !isLoading) {
      //Amplitude
      const event: SearchEvent = {
        TRAVEL_PRODUCT_TYPE: 'HOTELS',
        TRAVEL_FROM: '',
        TRAVEL_TO: destinationPoint?.id.toString() ?? '',
        TRAVEL_DESTINATION_TYPE: '',
        TRAVEL_TRIP_TYPE: '',
        TRAVEL_ADVANCED_PURCHASE: daysCount(checkInDate, checkOutDate).toString(),
        TRAVEL_DATE_FROM: checkInDate?.toString() ?? '',
        TRAVEL_DATE_TO: checkOutDate?.toString() ?? '',
        TRAVEL_TRIP_LENGTH: daysCount(checkInDate, checkOutDate).toString(),
        TRAVEL_PASSENGERS: '1',
        TRAVEL_ADULTS: '1',
        TRAVEL_CHILDREN: '0',
        TRAVEL_INFANTS: '0',
        TRAVEL_CLASS_OF_SERVICE: '',
        TRAVEL_ROOMS: '1',
        TRAVEL_WITH_BAGGAGE: '',
        TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
        SEARCHID: '',
      };

      amplitudelogEvent(amplitudeEvents.SELECT_TRAVEL_SEARCH, event, user);
      await dispatch(
        hotelsSearchParameters({
          destinationPoint,
          checkInDate,
          checkOutDate,
          traveler,
          branchCompany,
        }),
      );
      if (isHome) history.push('/hotels');
      else history.replace('/hotels');
    }
  }, [
    checkInDate,
    checkOutDate,
    destinationPoint,
    dispatch,
    history,
    isHome,
    isLoading,
    traveler,
    user,
    branchCompany,
  ]);

  const getBookingSelectValue = (data: TravelHotelContentLocationRequest | undefined) => {
    if (!data) return;

    return {
      name: data.name,
      detail: data.description,
    };
  };

  const renderDeparturePointSelect = () => (
    <ThemeProvider theme={customTheme}>
      <BookingSelectLocation
        label={t('hotelsSearch.destinationPoint.label')}
        detail={t('hotelsSearch.destinationPoint.detail')}
        value={getBookingSelectValue(destinationPoint)}
        icon={
          <Icon>
            <IconArrival />
          </Icon>
        }
        onChange={(value, isCity?: boolean) => {
          setDestinationPoint(value as TravelHotelContentLocationRequest);
        }}
        testId="hotel-search-destination"
      >
        {(selectProps: BookingSelectRenderProps) => (
          <HotelsLocationSearchContainer
            {...selectProps}
            placeHolder={t('hotelsSearch.destinationPoint.modalSearchPlaceholder')}
          />
        )}
      </BookingSelectLocation>
    </ThemeProvider>
  );

  const renderCheckInDateSelect = () => (
    <ThemeProvider theme={customTheme}>
      <BookingSelectDate
        label={t('hotelsSearch.checkInDate')}
        value={checkInDate ? dateFormatWeekend(checkInDate) : undefined}
        onChange={handleChangeDateSelect}
        icon={
          <Icon>
            <IconCalendar />
          </Icon>
        }
        testId="hotel-search-checkin-date"
      >
        {(selectProps: BookingSelectRenderProps) => (
          <DateRangePicker
            {...selectProps}
            minRange={1}
            type="check-in-out"
            currentInitialDate={checkInDate}
            currentFinalDate={checkOutDate}
          />
        )}
      </BookingSelectDate>
    </ThemeProvider>
  );

  const renderCheckOutDateSelect = () => (
    <ThemeProvider theme={customTheme}>
      <BookingSelectDate
        label={t('hotelsSearch.checkOutDate')}
        value={checkOutDate ? dateFormatWeekend(checkOutDate) : undefined}
        onChange={handleChangeDateSelect}
        icon={
          <Icon>
            <IconCalendar />
          </Icon>
        }
        testId="hotel-search-checkout-date"
      >
        {(selectProps: BookingSelectRenderProps) => (
          <DateRangePicker
            {...selectProps}
            minRange={1}
            type="check-in-out"
            editingInitialDate={false}
            currentInitialDate={checkInDate}
            currentFinalDate={checkOutDate}
          />
        )}
      </BookingSelectDate>
    </ThemeProvider>
  );

  const handleTravelerSelect = (item: GroupedListModel) => {
    setTraveler(allowedTravelers.find((traveler) => traveler.id === item.id));
  };

  const handleBranchCompanySelect = (item: GroupedListModel) => {
    if (branchCompanies) {
      setBranchCompany(branchCompanies?.find((branchCompany) => branchCompany.id === item.id));
    }

    return;
  };

  const transformedBranchCompanies = (): GroupedListModel[] =>
    branchCompanies ? branchCompanies?.map((b) => ({ id: b.id, name: b.corporateName })) : ([] as GroupedListModel[]);

  const handleCreateGuestTraveler = (guest: Guest) => {
    setTraveler({
      guest: true,
      favorite: false,
      userId: guest.userId || '',
      id: guest.id || '',
      name: guest.name,
      lastName: guest.lastName,
      email: guest.email,
      birthDate: guest.birthDate,
      phoneNumber: guest.phoneNumber,
      country: guest.country,
      documentNumber: guest.documentNumber,
      gender: guest.gender,
      passportNumber: guest.passportNumber,
    });
  };

  const renderTravelerSelect = () => {
    if (!user) return;
    if (!user.isTravelArranger) return null;

    return (
      <ThemeProvider theme={customTheme}>
        <TravelArrangerSelect
          title={t('travelArrangerSelector.placeholder')}
          buttonLabel={t('travelArrangerSelector.buttonLabel')}
          addGuestLabel={t('travelArrangerSelector.addGuest')}
          inviteAllowed={user.isGuestBookAllowed}
          options={transformedTravelerList}
          value={transformedSelectedTraveler}
          onChange={handleTravelerSelect}
          onCreateGuest={handleCreateGuestTraveler}
          isLoading={isLoadingAllowedTravelers}
          testId="hotel-search-traveler"
        />
      </ThemeProvider>
    );
  };

  const renderBranchCompanySelect = () => {
    if (!user) return;
    if (!branchCompanies) return;
    if (branchCompanies.length <= 0) return;

    return (
      <ThemeProvider theme={customTheme}>
        <BranchCompanySelect
          title={t('billingCompanySelector.placeholder')}
          buttonLabel={t('billingCompanySelector.buttonLabel')}
          options={transformedBranchCompanies()}
          value={transformedSelectedBranchCompany}
          onChange={handleBranchCompanySelect}
          isLoading={isLoadingBranchCompanies}
          testId="hotel-search-branch-company"
        />
      </ThemeProvider>
    );
  };

  return (
    <>
      <Container>
        <Fields>
          <Field colstart={1} colend={13} rowstart={1} rowend={5}>
            {renderDeparturePointSelect()}
          </Field>
          <Field colstart={13} colend={23} rowstart={1} rowend={5}>
            {renderCheckInDateSelect()}
          </Field>
          <Field colstart={23} colend={33} rowstart={1} rowend={5}>
            {renderCheckOutDateSelect()}
          </Field>
          {user && user.isTravelArranger && (
            <Field colstart={1} colend={13} rowstart={5} rowend={8}>
              {renderTravelerSelect()}
            </Field>
          )}
          {user && branchCompanies && branchCompanies.length > 0 && branchCompany && (
            <Field
              colstart={user && user.isTravelArranger ? 13 : 1}
              colend={user && user.isTravelArranger ? 23 : 13}
              rowstart={5}
              rowend={8}
            >
              {renderBranchCompanySelect()}
            </Field>
          )}
          <Field colstart={33} colend={34} rowstart={1} rowend={5}>
            <SearchButton
              disabled={!isValid || isLoadingAllowedTravelers || isLoading || !isBranchCompanyValid}
              onClick={handleClickSearchButton}
              value={t('hotelsSearch.searchButton')}
              testId="hotel-search-submit"
            >
              <IconSearch />
            </SearchButton>
          </Field>
        </Fields>
      </Container>

      <ResponseScreen
        show={showNoResults}
        text="Ops!"
        subText={t('hotelsSearch.searchError')}
        background={BackgroundTypes.Mountain}
        buttonOnClick={() => setShowNoResults(false)}
        buttonText={t('hotelsSearch.backButton')}
      />
    </>
  );
};

export default withTheme(DesktopHotelsSearch);
